import { message } from "antd";
import { useCallback, useState } from "react";
import ContractService from "src/services/ContractService";
import HRService from "src/services/HumanResource";

export function useContracts() {
  const [contracts, setContracts] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [previewContract, setPreviewContract] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [signatoryConfig, setSignatoryConfig] = useState({ configured: false });
  const [listPersponel, setListPersonel] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [params, setParams] = useState({
    page: 1,
    per_page: 10,
    keyword: "",
  });

  // GET PROVINCES LIST
  const fetchProvinces = useCallback(async () => {
    try {
      const res = await HRService.getProvince({ country_id: 1 });
      const list = res?.success || res?.data || [];
      if (Array.isArray(list)) {
        setProvinces(list);
      }
    } catch (err) {
      console.error("Fetch provinces error:", err);
    }
  }, []);

  // GET ALL CONTRACTS
  const fetchContracts = useCallback(async (newParams = params) => {
    setLoading(true);
    try {
      const { page, per_page, keyword, ...rawFilter } = newParams || {};

      const cleanFilter = {};
      Object.keys(rawFilter).forEach((key) => {
        const val = rawFilter[key];
        if (val !== undefined && val !== null && val !== "") {
          cleanFilter[key] = val;
        }
      });

      const requestParams = {
        page: page || 1,
        per_page: per_page || 10,
      };

      if (keyword && String(keyword).trim()) {
        requestParams.keyword = String(keyword).trim();
      }

      if (Object.keys(cleanFilter).length > 0) {
        requestParams.filter = cleanFilter;
      }

      const res = await ContractService.getAllContracts(requestParams);

      const responseData = res?.data || res?.success || { data: [], total: 0 };
      setContracts(responseData);
    } catch (err) {
      console.error("Fetch contracts error:", err);
      message.error("Không thể tải danh sách hợp đồng!");
    } finally {
      setLoading(false);
    }
  }, [params]);

  // GET CONTRACT DETAIL
  const fetchDetail = useCallback(async (id) => {
    setPreviewLoading(true);
    try {
      const res = await ContractService.getContractDetail(id);
      const data = res?.data || res?.success || null;
      setPreviewContract(data);
      return data;
    } catch (err) {
      console.error("Fetch contract detail error:", err);
      setPreviewContract(null);
      return null;
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // GET ALL PERSONNEL DETAILS
  const fetchAllPersonel = useCallback(async () => {
    try {
      const res = await ContractService.getAllPersonel({ detail: 1 });
      const data = res?.data || res?.success || [];
      setListPersonel(data);
      return data;
    } catch (err) {
      console.error("Fetch personnel error:", err);
      setListPersonel([]);
      return [];
    }
  }, []);

  // CREATE / UPDATE
  const saveContract = useCallback(async (editingId, type, formValues) => {
    try {
      const formData = new FormData();
      formData.append("type", type);

      Object.entries(formValues || {}).forEach(([key, value]) => {
        if (["file", "file_url"].includes(key)) return;
        if (value !== null && value !== undefined) {
          formData.append(`data[${key}]`, value);
        }
      });

      let res;
      if (editingId) {
        res = await ContractService.updateContract(editingId, formData);
        message.success(res?.message || "Chỉnh sửa hợp đồng thành công!");
      } else {
        res = await ContractService.createContract(formData);
        message.success(res?.message || "Tạo mới hợp đồng thành công!");
      }

      fetchContracts(params);
      return res;
    } catch (err) {
      console.error("Save contract error:", err);
      const errMsg = err?.response?.data?.message || err?.message || "Lưu hợp đồng thất bại!";
      message.error(errMsg);
      throw err;
    }
  }, [params, fetchContracts]);

  // DELETE
  const deleteContract = useCallback(async (id) => {
    try {
      const res = await ContractService.deleteContract(id);
      message.success(res?.message || "Xóa hợp đồng thành công!");
      fetchContracts(params);
    } catch (err) {
      console.error("Delete contract error:", err);
      message.error(err?.response?.data?.message || "Xóa hợp đồng thất bại!");
    }
  }, [params, fetchContracts]);

  // UPLOAD SCAN FILE
  const handleUploadFiles = useCallback(async (contract, files) => {
    try {
      const file = files?.[0];
      if (!file) return;

      const contractId = contract?.id || contract?.ID;
      const formData = new FormData();
      formData.append("file", file, file.name);

      const res = await ContractService.uploadScanFile(contractId, formData);
      message.success(res?.message || "Tải file scan chữ ký thành công!");

      fetchContracts(params);
      if (contractId) {
        fetchDetail(contractId);
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error(err?.response?.data?.message || "Tải file scan thất bại!");
    }
  }, [params, fetchContracts, fetchDetail]);

  // SAVE CONTRACT SIGNATURE
  const saveContractSignature = useCallback(async (id, signatureData) => {
    try {
      const res = await ContractService.signContract(id, signatureData);
      message.success(res?.message || "Ký hợp đồng thành công!");
      fetchContracts(params);
      if (id) {
        await fetchDetail(id);
      }
      return res;
    } catch (err) {
      console.error("Sign contract error:", err);
      const errMsg = err?.response?.data?.message || err?.message || "Ký hợp đồng thất bại!";
      message.error(errMsg);
      throw err;
    }
  }, [params, fetchContracts, fetchDetail]);

  const fetchSignatoryConfig = useCallback(async () => {
    try {
      const res = await ContractService.getSignatoryConfig();
      const config = res?.data?.data || res?.data?.success || res?.data || res?.success || null;
      setSignatoryConfig(config || { configured: false });
      return config;
    } catch (err) {
      console.error("Fetch signatory config error:", err);
      setSignatoryConfig({ configured: false });
      return null;
    }
  }, []);

  const terminateContract = useCallback(
    async (id, payload) => {
      try {
        const res = await ContractService.terminateContract(id, payload);
        message.success(res?.message || "Kết thúc hợp đồng thành công!");
        fetchContracts(params);
        return res;
      } catch (err) {
        console.error("Terminate contract error:", err);
        const errMsg = err?.response?.data?.message || err?.message || "Kết thúc hợp đồng thất bại!";
        message.error(errMsg);
        throw err;
      }
    },
    [params, fetchContracts]
  );

  const saveSignatoryConfig = useCallback(async (payload) => {
    const res = await ContractService.saveSignatoryConfig(payload);
    const config = res?.data?.data || res?.data?.success || res?.data || res?.success || null;
    if (!config) throw new Error("Không nhận được cấu hình chữ ký từ máy chủ.");
    setSignatoryConfig(config);
    return config;
  }, []);

  return {
    contracts,
    loading,
    params,
    setParams,
    fetchContracts,
    saveContract,
    deleteContract,
    terminateContract,
    handleUploadFiles,
    fetchDetail,
    previewContract,
    previewLoading,
    fetchAllPersonel,
    listPersponel,
    provinces,
    fetchProvinces,
    saveContractSignature,
    signatoryConfig,
    fetchSignatoryConfig,
    saveSignatoryConfig,
  };
}
